import { type FirebaseApp } from "firebase/app";
import { getFirebaseApp } from "../lib/firebase";
import * as Sentry from "@sentry/react";
import {
  getRemoteConfig,
  fetchAndActivate,
  getValue,
  type RemoteConfig,
  ensureInitialized,
  getAll,
  onConfigUpdate,
  type ConfigUpdate,
} from "firebase/remote-config";
import type { IRemoteConfigService } from "@repo/utils";
import { remoteConfigDefaults } from "../config/remote-config.defaults";

class RemoteConfigService implements IRemoteConfigService {
  private app: FirebaseApp | null = null;
  private remoteConfig: RemoteConfig | null = null;

  private config = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
  };

  async initialize(): Promise<void> {
    if (!this.config.apiKey) {
      console.warn("Firebase config missing. Remote Config will not work.");
      return;
    }

    try {
      this.app = getFirebaseApp();
      this.remoteConfig = getRemoteConfig(this.app);

      // Set fetch interval for dev vs prod
      const isDev = import.meta.env.DEV;
      this.remoteConfig.settings.minimumFetchIntervalMillis = isDev
        ? 10000 // 10 seconds for dev
        : 43200000; // 12 hours for prod

      // Set default values from central config
      this.remoteConfig.defaultConfig = remoteConfigDefaults;

      await ensureInitialized(this.remoteConfig);
    } catch (error) {
      console.error("Failed to initialize Firebase Remote Config:", error);
      Sentry.captureException(error);
    }
  }

  async fetchAndActivate(): Promise<boolean> {
    if (!this.remoteConfig) return false;
    try {
      return await fetchAndActivate(this.remoteConfig);
    } catch (error) {
      console.error("Failed to fetch and activate remote config:", error);
      Sentry.captureException(error);
      return false;
    }
  }

  getString(key: string): string {
    if (!this.remoteConfig) return "";
    return getValue(this.remoteConfig, key).asString();
  }

  getNumber(key: string): number {
    if (!this.remoteConfig) return 0;
    return getValue(this.remoteConfig, key).asNumber();
  }

  getBoolean(key: string): boolean {
    if (!this.remoteConfig) return false;
    return getValue(this.remoteConfig, key).asBoolean();
  }

  getJSON(key: string): Record<string, any> {
    if (!this.remoteConfig) return {};
    try {
      const val = getValue(this.remoteConfig, key).asString();
      if (!val || val === "") return {};
      return JSON.parse(val);
    } catch (e) {
      console.warn(`Failed to parse JSON for key ${key}`, e);
      Sentry.captureException(e, { extra: { key } });
      return {};
    }
  }

  async setUserProperty(name: string, value: string): Promise<void> {
    console.log(`Setting user property ${name} to ${value} for Web`);
    // Note: Implementation usually involves firebase analytics:
    // setAnalyticsUserProperty(analytics, name, value);
  }

  getAllValues(): Record<string, any> {
    if (!this.remoteConfig) return {};
    const all = getAll(this.remoteConfig);
    const result: Record<string, any> = {};

    Object.keys(all).forEach((key) => {
      const defaultValue = remoteConfigDefaults[key];

      if (typeof defaultValue === "boolean") {
        result[key] = this.getBoolean(key);
      } else if (typeof defaultValue === "number") {
        result[key] = this.getNumber(key);
      } else if (typeof defaultValue === "object" && defaultValue !== null) {
        result[key] = this.getJSON(key);
      } else {
        result[key] = this.getString(key);
      }
    });

    return result;
  }

  onUpdate(callback: () => void): () => void {
    if (!this.remoteConfig) return () => {};
    return onConfigUpdate(this.remoteConfig, {
      next: (configUpdate: ConfigUpdate) => {
        console.log("Remote Config updated:", configUpdate);
        fetchAndActivate(this.remoteConfig!)
          .then(() => {
            callback();
          })
          .catch((err) => {
            console.error("Failed to fetch on config update", err);
          });
      },
      error: (error) => {
        console.error("Remote Config update error:", error);
      },
      complete: () => {
        console.log("Remote Config update stream completed");
      },
    });
  }

  logFlagExposure(key: string, value: any): void {
    console.log(`[Analytics] Flag exposure: ${key} = ${value}`);
    // implementation: logEvent(analytics, 'feature_flag_exposure', { key, value });
  }
}

export const remoteConfigService = new RemoteConfigService();
